import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  getFinancingRequests,
  updateFinancingStatus,
  approveFinancing,
  rejectFinancing,
  addFinancingNote,
} from "@/services/financingRequestService";
import type { FinancingRequestDoc, FirestoreDate } from "@/types/financingRequest";
import { FINANCING_STATUS_LABELS, FINANCING_SOURCE_LABELS } from "@/types/financingRequest";
import { queryKeys } from "@/lib/queryKeys";
import {
  ArrowLeft,
  User,
  Car,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquarePlus,
  Loader2,
  FileText,
  Mail,
  Phone,
  MapPin,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function formatFirestoreDate(value: FirestoreDate | null | undefined): string {
  if (value == null) return "--";
  const d =
    value instanceof Date
      ? value
      : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function"
        ? (value as { toDate: () => Date }).toDate()
        : "seconds" in value
          ? new Date((value as { seconds: number }).seconds * 1000)
          : null;
  return d
    ? d.toLocaleDateString("es-DO", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "--";
}

function formatCurrency(amount: number): string {
  return `RD$ ${amount.toLocaleString("es-DO")}`;
}

function getDisplayName(r: FinancingRequestDoc): string {
  const first = r.customerInfo?.firstName ?? r.firstName ?? "";
  const last = r.customerInfo?.lastName ?? r.lastName ?? "";
  return [first, last].filter(Boolean).join(" ").trim() || "--";
}

function getDisplayPhone(r: FinancingRequestDoc): string {
  return r.customerInfo?.phone ?? r.phone ?? "--";
}

function getDisplayEmail(r: FinancingRequestDoc): string {
  return r.customerInfo?.email ?? r.email ?? "";
}

function getRequestedAmount(r: FinancingRequestDoc): number {
  return r.requestedAmount ?? r.loanAmount ?? 0;
}

function getVehicleSummary(r: FinancingRequestDoc): string {
  const parts = [r.vehicleBrand, r.vehicleModel, r.vehicleYear ? String(r.vehicleYear) : null].filter(Boolean);
  return parts.length ? parts.join(" ") : r.vehicleName ?? r.vehicleId ?? "--";
}

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pendiente: "secondary",
  en_revision: "default",
  pre_aprobado: "default",
  aprobado: "default",
  rechazado: "destructive",
  completado: "default",
  cancelado: "outline",
};

const STATUS_COLORS: Record<string, string> = {
  pendiente: "bg-yellow-100 text-yellow-700 border-yellow-300",
  en_revision: "bg-blue-100 text-blue-700 border-blue-300",
  pre_aprobado: "bg-cyan-100 text-cyan-700 border-cyan-300",
  aprobado: "bg-green-100 text-green-700 border-green-300",
  rechazado: "bg-red-100 text-red-700 border-red-300",
  completado: "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelado: "bg-gray-100 text-gray-700 border-gray-300",
};

function Spec({ label, value }: { label: string; value?: string | number | null }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/50 last:border-0">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{String(value)}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export function FinancingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  // Fetch from the list and find the one we need (no getById service exists)
  const { data: requests = [], isLoading } = useQuery({
    queryKey: queryKeys.financingRequests,
    queryFn: getFinancingRequests,
  });

  const request = requests.find((r) => r.id === id) ?? null;

  const handleStatusChange = async (status: string) => {
    if (!id) return;
    setActionLoading(status);
    try {
      await updateFinancingStatus(id, status);
      await queryClient.invalidateQueries({ queryKey: queryKeys.financingRequests });
      toast.success(`Estado actualizado a: ${FINANCING_STATUS_LABELS[status] ?? status}`);
    } catch {
      toast.error("Error al cambiar el estado");
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    setActionLoading("aprobado");
    try {
      await approveFinancing(id, "admin");
      await queryClient.invalidateQueries({ queryKey: queryKeys.financingRequests });
      toast.success("Solicitud aprobada");
    } catch {
      toast.error("Error al aprobar");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    if (!rejectReason.trim()) {
      toast.error("Ingresa un motivo de rechazo");
      return;
    }
    setActionLoading("rechazado");
    try {
      await rejectFinancing(id, rejectReason);
      await queryClient.invalidateQueries({ queryKey: queryKeys.financingRequests });
      toast.success("Solicitud rechazada");
      setRejectReason("");
    } catch {
      toast.error("Error al rechazar");
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddNote = async () => {
    if (!id || !adminNote.trim()) {
      toast.error("Escribe una nota");
      return;
    }
    setActionLoading("note");
    try {
      await addFinancingNote(id, adminNote, "admin");
      await queryClient.invalidateQueries({ queryKey: queryKeys.financingRequests });
      toast.success("Nota agregada");
      setAdminNote("");
    } catch {
      toast.error("Error al agregar nota");
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/financings"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Solicitud de financiamiento</h1>
        </div>
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/financings"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Solicitud de financiamiento</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Solicitud no encontrada</p>
            <Button asChild className="mt-4">
              <Link to="/financings">Volver a financiamientos</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/financings">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Volver</span>
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl font-semibold">{getDisplayName(request)}</h1>
              <Badge variant="outline" className={STATUS_COLORS[request.status] ?? ""}>
                {FINANCING_STATUS_LABELS[request.status] ?? request.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {formatFirestoreDate(request.createdAt)}
              {request.source ? ` · ${FINANCING_SOURCE_LABELS[request.source] ?? request.source}` : ""}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Customer info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              Información del cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{getDisplayName(request)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{getDisplayPhone(request)}</span>
              </div>
              {getDisplayEmail(request) && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{getDisplayEmail(request)}</span>
                </div>
              )}
              {(request.customerInfo?.cedula ?? request.cedula) && (
                <Spec label="Cédula" value={request.customerInfo?.cedula ?? request.cedula} />
              )}
              {(request.customerInfo?.address ?? request.address) && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{request.customerInfo?.address ?? request.address}</span>
                </div>
              )}
              {(request.customerInfo?.city ?? request.city) && (
                <Spec label="Ciudad" value={request.customerInfo?.city ?? request.city} />
              )}
              {request.customerInfo?.employmentStatus && (
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <span>{request.customerInfo.employmentStatus}</span>
                </div>
              )}
              {request.customerInfo?.monthlyIncome != null && (
                <Spec label="Ingreso mensual" value={formatCurrency(request.customerInfo.monthlyIncome)} />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Vehicle */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Car className="h-4 w-4" />
              Vehículo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {request.vehicleImage && (
              <img src={request.vehicleImage} alt="" className="h-32 w-full rounded-lg object-cover border border-border mb-3" />
            )}
            <Spec label="Vehículo" value={getVehicleSummary(request)} />
            {request.vehiclePrice != null && (
              <Spec label="Precio" value={formatCurrency(request.vehiclePrice)} />
            )}
            {request.vehicleId && (
              <div className="pt-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/vehicles/${request.vehicleId}`}>Ver vehículo</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Financing amounts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4" />
              Montos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Spec label="Monto solicitado" value={formatCurrency(getRequestedAmount(request))} />
            <Spec label="Inicial" value={formatCurrency(request.downPayment ?? 0)} />
            <Spec label="Plazo" value={request.termMonths != null ? `${request.termMonths} meses` : undefined} />
            {request.estimatedMonthlyPayment != null && (
              <Spec label="Cuota estimada" value={formatCurrency(request.estimatedMonthlyPayment)} />
            )}
          </CardContent>
        </Card>

        {/* Dates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-4 w-4" />
              Fechas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Spec label="Creada" value={formatFirestoreDate(request.createdAt)} />
            <Spec label="Actualizada" value={formatFirestoreDate(request.updatedAt)} />
            <Spec label="Revisada" value={formatFirestoreDate(request.reviewedAt)} />
            <Spec label="Aprobada" value={formatFirestoreDate(request.approvedAt)} />
            <Spec label="Completada" value={formatFirestoreDate(request.completedAt)} />
          </CardContent>
        </Card>
      </div>

      {/* Rejection reason */}
      {request.rejectionReason && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
          <span className="font-medium text-destructive">Motivo de rechazo:</span>{" "}
          {request.rejectionReason}
        </div>
      )}

      {/* Customer notes */}
      {request.customerNotes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notas del cliente</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{request.customerNotes}</p>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Acciones</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={actionLoading !== null || request.status === "en_revision"}
              onClick={() => handleStatusChange("en_revision")}
              className="gap-2"
            >
              {actionLoading === "en_revision" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
              En Revisión
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={actionLoading !== null || request.status === "aprobado"}
              onClick={handleApprove}
              className="gap-2 text-green-700 border-green-300 hover:bg-green-50"
            >
              {actionLoading === "aprobado" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              Aprobar
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={actionLoading !== null || request.status === "rechazado"}
              onClick={handleReject}
              className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/5"
            >
              {actionLoading === "rechazado" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              Rechazar
            </Button>
          </div>

          {/* Reject reason input */}
          {request.status !== "rechazado" && (
            <div className="grid gap-2">
              <label className="text-sm font-medium">Motivo de rechazo (requerido para rechazar)</label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Escribe el motivo del rechazo..."
                rows={2}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquarePlus className="h-4 w-4" />
            Notas administrativas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {request.adminNotes && typeof request.adminNotes === "string" && (
            <div className="rounded-lg border p-3 text-sm bg-muted/50">{request.adminNotes}</div>
          )}
          {Array.isArray(request.adminNotes) &&
            (request.adminNotes as Array<{ note: string; author: string; createdAt: string }>).map((n, i) => (
              <div key={i} className="rounded-lg border p-3 text-sm bg-muted/50">
                <p>{n.note}</p>
                <p className="text-xs text-muted-foreground mt-1">{n.author} -- {n.createdAt}</p>
              </div>
            ))}

          <div className="flex gap-2">
            <Textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Agregar una nota..."
              rows={2}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={actionLoading === "note" || !adminNote.trim()}
              onClick={handleAddNote}
              className="gap-2 self-end"
            >
              {actionLoading === "note" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
              Agregar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
